import { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, type LocalVideoTrack, type RemoteVideoTrack, type RemoteAudioTrack } from 'livekit-client';
import { useAppStore, nameOf } from '../store/useAppStore.js';
import { RTC_CONFIG, CALL_ROOM_OPTIONS, pairedRoomName } from '../lib/livekit.js';
import { requestToken, getSessionForRating } from '../api/client.js';
import { instrumentRoom } from '../debug/instrumentRoom.js';
import { findScreenShareTrack } from '../lib/screenShare.js';

export interface UseCallRoomResult {
  room: Room | null;
  localVideo: LocalVideoTrack | null;
  remoteVideo: RemoteVideoTrack | null;
  remoteAudio: RemoteAudioTrack | null;
  remoteScreen: RemoteVideoTrack | null;
  localScreen: LocalVideoTrack | null;
  screenShareEnabled: boolean;
  /** Auth uuid of the peer publishing camera video — a key. Resolve a name
   *  through the roster before showing it to anyone. */
  remoteIdentity: string | null;
  /** Display name LiveKit carries on the peer's token — a label, and fresher
   *  than the roster. Null until they publish, or if their token had no name. */
  remoteName: string | null;
  micEnabled: boolean;
  camEnabled: boolean;
  toggleMic(): Promise<void>;
  toggleCam(): Promise<void>;
  toggleScreenShare(): Promise<void>;
  hangup(): Promise<void>;
  error: string | null;
}

export function useCallRoom(): UseCallRoomResult {
  const view = useAppStore((s) => s.view);
  const userId = useAppStore((s) => s.userId);
  const callPeer = useAppStore((s) => s.callPeer);
  const setView = useAppStore((s) => s.setView);
  const setCallPeer = useAppStore((s) => s.setCallPeer);
  const setLastSessionId = useAppStore((s) => s.setLastSessionId);
  const setLastCallPeer = useAppStore((s) => s.setLastCallPeer);

  const [room, setRoom] = useState<Room | null>(null);
  const [localVideo, setLocalVideo] = useState<LocalVideoTrack | null>(null);
  const [remoteVideo, setRemoteVideo] = useState<RemoteVideoTrack | null>(null);
  const [remoteAudio, setRemoteAudio] = useState<RemoteAudioTrack | null>(null);
  const [remoteScreen, setRemoteScreen] = useState<RemoteVideoTrack | null>(null);
  const [localScreen, setLocalScreen] = useState<LocalVideoTrack | null>(null);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [remoteIdentity, setRemoteIdentity] = useState<string | null>(null);
  const [remoteName, setRemoteName] = useState<string | null>(null);
  // Mirrors remoteName for the end-of-call handoff: cleanupAndExit clears the
  // React state before transitionToRating runs, and the label has to survive.
  const remoteNameRef = useRef<string | null>(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const roomRef = useRef<Room | null>(null);

  const active = view === 'call' && !!userId && !!callPeer;

  async function transitionToRating(roomName: string, peer: string | null) {
    // lastCallPeer is a label — RatingView prints it and the safety report
    // carries it. Prefer the name off the peer's token, fall back to the
    // roster; null leaves RatingView on its "your peer" wording.
    setLastCallPeer(remoteNameRef.current || nameOf(useAppStore.getState().users, peer));
    try {
      const id = await getSessionForRating(roomName);
      setLastSessionId(id);
    } catch {
      // Network or RPC error — RatingView falls back to Skip-only.
      setLastSessionId(null);
    }
    setView('rate');
  }

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    remoteNameRef.current = null;
    const peer = callPeer!;
    const callRoomName = pairedRoomName(userId!, peer);

    function cleanupAndExit() {
      const r = roomRef.current;
      roomRef.current = null;
      setRoom(null); setLocalVideo(null); setRemoteVideo(null); setRemoteAudio(null); setRemoteIdentity(null); setRemoteName(null);
      setRemoteScreen(null); setLocalScreen(null); setScreenShareEnabled(false);
      setCallPeer(null);
      // Best-effort session-id fetch happens inside transitionToRating; it
      // sets view to 'rate' when done (or immediately if the fetch fails).
      void transitionToRating(callRoomName, peer);
      r?.disconnect().catch(() => {});
    }

    (async () => {
      try {
        const tok = await requestToken({ room: callRoomName });
        if (cancelled) return;
        const r = new Room(CALL_ROOM_OPTIONS);
        instrumentRoom(r, `call:${callRoomName}`);

        // The token's `name` field is server-authoritative and current, unlike
        // this client's roster snapshot — but LiveKit returns '' (not undefined)
        // when it's unset, so an empty string must fall through rather than be
        // stored as the peer's label. Only trust it for the actual peer: a
        // captioner participant can also be in this room.
        function captureRemoteName(participant: { identity: string; name?: string }) {
          if (participant.identity !== peer) return;
          const name = participant.name || null;
          if (!name) return;
          remoteNameRef.current = name;
          setRemoteName(name);
        }

        r.on(RoomEvent.ParticipantConnected, captureRemoteName);
        r.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          // Capture the name off any subscribed track, not just camera video —
          // a call can end before the peer's camera track subscribes.
          captureRemoteName(participant);
          if (track.kind === Track.Kind.Video) {
            if (pub.source === Track.Source.ScreenShare) {
              setRemoteScreen(track as RemoteVideoTrack);
            } else {
              setRemoteVideo(track as RemoteVideoTrack);
              setRemoteIdentity(participant.identity);
            }
          } else if (track.kind === Track.Kind.Audio) {
            setRemoteAudio(track as RemoteAudioTrack);
          }
        });
        // Clear the screen slot when the remote stops sharing so the layout
        // reverts. Camera unsubscribe is intentionally NOT handled here (see the
        // task note) to preserve today's behavior when a peer toggles their cam.
        r.on(RoomEvent.TrackUnsubscribed, (track, pub) => {
          if (track.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare) {
            setRemoteScreen(null);
          }
        });
        // Fires both when WE stop via our button and when the user clicks the
        // browser's native "Stop sharing" bar — single source of truth for
        // tearing down local-share state.
        r.on(RoomEvent.LocalTrackUnpublished, (pub) => {
          if (pub.source === Track.Source.ScreenShare) {
            setLocalScreen(null);
            setScreenShareEnabled(false);
          }
        });
        r.on(RoomEvent.ParticipantDisconnected, (p) => {
          if (p.identity === peer) {
            cleanupAndExit();
          }
        });
        // Local room died (peer hangup races, admin force-end, server kick).
        // ParticipantDisconnected alone isn't enough — the side that gets
        // Disconnected first never sees the peer leave.
        r.on(RoomEvent.Disconnected, () => {
          cleanupAndExit();
        });

        await r.connect(tok.wsUrl, tok.token, { rtcConfig: RTC_CONFIG });
        if (cancelled) { await r.disconnect(); return; }
        // Participants who joined before we connected never fire
        // ParticipantConnected for us — pick their name up directly.
        for (const p of r.remoteParticipants.values()) captureRemoteName(p);

        await r.localParticipant.enableCameraAndMicrophone();
        for (const pub of r.localParticipant.videoTrackPublications.values()) {
          if (pub.track) { setLocalVideo(pub.track as LocalVideoTrack); break; }
        }
        roomRef.current = r;
        setRoom(r);
        setMicEnabled(true);
        setCamEnabled(true);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      const r = roomRef.current;
      roomRef.current = null;
      setRoom(null); setLocalVideo(null); setRemoteVideo(null); setRemoteAudio(null); setRemoteIdentity(null); setRemoteName(null);
      setRemoteScreen(null); setLocalScreen(null); setScreenShareEnabled(false);
      r?.disconnect().catch(() => {});
    };
  }, [active, userId, callPeer, setCallPeer, setView, setLastSessionId, setLastCallPeer]);

  return {
    room, localVideo, remoteVideo, remoteAudio, remoteScreen, localScreen, screenShareEnabled,
    remoteIdentity, remoteName, micEnabled, camEnabled, error,
    async toggleMic() {
      if (!roomRef.current) return;
      const lp = roomRef.current.localParticipant;
      const next = !lp.isMicrophoneEnabled;
      await lp.setMicrophoneEnabled(next);
      setMicEnabled(next);
    },
    async toggleCam() {
      if (!roomRef.current) return;
      const lp = roomRef.current.localParticipant;
      const next = !lp.isCameraEnabled;
      await lp.setCameraEnabled(next);
      setCamEnabled(next);
    },
    async toggleScreenShare() {
      const r = roomRef.current;
      if (!r) return;
      const lp = r.localParticipant;
      const next = !lp.isScreenShareEnabled;
      try {
        await lp.setScreenShareEnabled(next, { audio: false });
      } catch {
        // User dismissed the OS picker, or capture failed — treat as "not sharing".
        setScreenShareEnabled(false);
        setLocalScreen(null);
        return;
      }
      if (next) {
        const track = findScreenShareTrack(lp.videoTrackPublications.values());
        if (!track) {
          // Resolved without a published track (picker cancelled on some browsers).
          setScreenShareEnabled(false);
          setLocalScreen(null);
          return;
        }
        setLocalScreen(track);
        setScreenShareEnabled(true);
      } else {
        setLocalScreen(null);
        setScreenShareEnabled(false);
      }
    },
    async hangup() {
      const r = roomRef.current;
      roomRef.current = null;
      // Capture room name before clearing peer (pairedRoomName uses it).
      const peer = callPeer;
      const roomName = userId && peer ? pairedRoomName(userId, peer) : null;
      setCallPeer(null);
      if (roomName) {
        void transitionToRating(roomName, peer);
      } else {
        setView('lobby');
      }
      await r?.disconnect().catch(() => {});
    },
  };
}
